import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export type DsTooltipPlacement = 'auto' | 'top' | 'bottom' | 'left' | 'right';
export type DsTooltipArrowAlign = 'start' | 'center' | 'end';

interface DsTooltipVisibleBounds {
  top: number;
  right: number;
  bottom: number;
  left: number;
  width: number;
  height: number;
}

@Component({
  selector: 'ds-tooltip',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="ds-tooltip" [class.ds-tooltip--open]="open">
      <button
        #button
        type="button"
        class="ds-tooltip__button"
        [class.ds-tooltip__button--open]="open"
        [attr.aria-label]="ariaLabel"
        [attr.aria-expanded]="open"
        (click)="toggle()"
      >
        <svg class="ds-tooltip__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M11 17h2v-6h-2v6Zm1-8a1.15 1.15 0 1 0 0-2.3A1.15 1.15 0 0 0 12 9Zm0 13a10 10 0 1 1 0-20 10 10 0 0 1 0 20Z" />
        </svg>
      </button>

      <section
        #bubble
        class="ds-tooltip__bubble"
        [class.ds-tooltip__bubble--visible]="open"
        [ngClass]="bubbleClasses"
        [style.--ds-tooltip-bubble-left]="bubbleLeftStyle"
        [style.--ds-tooltip-bubble-top]="bubbleTopStyle"
        [style.--ds-tooltip-arrow-x]="arrowXStyle"
        [style.--ds-tooltip-arrow-y]="arrowYStyle"
        [attr.aria-hidden]="!open"
        role="tooltip"
      >
        <div class="ds-tooltip__surface">
          <div class="ds-tooltip__content">
            @if (hasTitle) {
              <div class="ds-tooltip__title-row">
                @if (hasMarker) {
                  <span
                    class="ds-tooltip__marker"
                    [style.background]="markerColor"
                    aria-hidden="true"
                  ></span>
                }
                <strong class="ds-tooltip__title">{{ title }}</strong>
              </div>
            } @else if (hasMarker) {
              <span
                class="ds-tooltip__marker"
                [style.background]="markerColor"
                aria-hidden="true"
              ></span>
            }

            <div class="ds-tooltip__description">
              <ng-content>{{ text }}</ng-content>
            </div>
          </div>
        </div>
      </section>
    </span>
  `,
  styles: [`
    :host {
      display: inline-flex;
      position: relative;
      font-family: 'Sura Sans', system-ui, sans-serif;
      line-height: 0;
      vertical-align: middle;
      z-index: 0;
    }

    :host(.ds-tooltip-host--open) {
      z-index: var(--ds-tooltip-bubble-z-index);
    }

    .ds-tooltip {
      position: relative;
      display: inline-flex;
    }

    .ds-tooltip--open {
      z-index: var(--ds-tooltip-bubble-z-index);
    }

    .ds-tooltip__button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: var(--ds-tooltip-button-container-size);
      height: var(--ds-tooltip-button-container-size);
      padding: 0;
      border: 0;
      border-radius: var(--ds-tooltip-button-border-radius);
      background: var(--ds-tooltip-button-background);
      color: var(--ds-tooltip-button-foreground);
      cursor: pointer;
      outline: none;
    }

    .ds-tooltip__button:hover,
    .ds-tooltip__button--open {
      background: var(--ds-tooltip-button-hover-overlay);
    }

    .ds-tooltip__button:focus-visible {
      outline: var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: var(--ds-focus-gap);
    }

    .ds-tooltip__icon {
      display: block;
      width: var(--ds-tooltip-button-icon-size);
      height: var(--ds-tooltip-button-icon-size);
      fill: currentColor;
    }

    .ds-tooltip__bubble {
      position: absolute;
      z-index: var(--ds-tooltip-bubble-z-index);
      width: max-content;
      max-width: var(--ds-tooltip-bubble-max-width);
      left: var(--ds-tooltip-bubble-left);
      top: var(--ds-tooltip-bubble-top);
      line-height: normal;
      opacity: 0;
      pointer-events: none;
      visibility: hidden;
    }

    .ds-tooltip__bubble--visible {
      opacity: 1;
      visibility: visible;
    }

    .ds-tooltip__surface {
      position: relative;
      box-sizing: border-box;
      max-width: var(--ds-tooltip-bubble-max-width);
      color: var(--ds-tooltip-bubble-foreground);
      --ds-tooltip-arrow-overlap: 2px;
      --ds-tooltip-arrow-border-visual-width: calc(var(--ds-tooltip-bubble-border-width) * 1.414);
    }

    .ds-tooltip__content {
      position: relative;
      z-index: 2;
      box-sizing: border-box;
      max-width: var(--ds-tooltip-bubble-max-width);
      padding: var(--ds-tooltip-bubble-padding-y) var(--ds-tooltip-bubble-padding-x);
      border: var(--ds-tooltip-bubble-border-width) solid var(--ds-tooltip-bubble-border-color);
      border-radius: var(--ds-tooltip-bubble-border-radius);
      background: var(--ds-tooltip-bubble-background);
      box-shadow: var(--ds-shadow-l2);
    }

    .ds-tooltip__title-row {
      display: flex;
      align-items: center;
      gap: var(--ds-tooltip-bubble-title-gap);
      margin-bottom: var(--ds-tooltip-bubble-content-gap);
      min-width: 0;
    }

    .ds-tooltip__title {
      min-width: 0;
      color: inherit;
      font-size: var(--ds-tooltip-bubble-title-font-size);
      font-weight: var(--ds-tooltip-bubble-title-font-weight);
      line-height: var(--ds-tooltip-bubble-title-line-height);
      overflow-wrap: anywhere;
    }

    .ds-tooltip__marker {
      display: inline-flex;
      width: var(--ds-tooltip-bubble-marker-size);
      height: var(--ds-tooltip-bubble-marker-size);
      flex: 0 0 var(--ds-tooltip-bubble-marker-size);
      box-sizing: border-box;
      border: var(--ds-tooltip-bubble-marker-border-width) solid var(--ds-tooltip-bubble-marker-border-color);
      border-radius: 50%;
    }

    .ds-tooltip__description {
      display: flex;
      min-width: 0;
      flex-direction: column;
      gap: var(--ds-tooltip-bubble-content-gap);
      color: inherit;
      font-size: var(--ds-tooltip-bubble-description-font-size);
      font-weight: var(--ds-tooltip-bubble-description-font-weight);
      line-height: var(--ds-tooltip-bubble-description-line-height);
      overflow-wrap: anywhere;
    }

    :host ::ng-deep .ds-tooltip__description > * {
      margin: 0;
    }

    :host ::ng-deep .ds-tooltip__description > ul,
    :host ::ng-deep .ds-tooltip__description > ol {
      display: flex;
      flex-direction: column;
      gap: var(--ds-tooltip-bubble-list-gap);
      padding-inline-start: var(--ds-tooltip-bubble-list-indent);
    }

    :host ::ng-deep .ds-tooltip__description li {
      margin: 0;
      padding: 0;
    }

    .ds-tooltip__surface::before,
    .ds-tooltip__surface::after {
      content: '';
      position: absolute;
      box-sizing: border-box;
      pointer-events: none;
    }

    .ds-tooltip__surface::before {
      z-index: 2;
      background: var(--ds-tooltip-bubble-border-color);
    }

    .ds-tooltip__surface::after {
      width: var(--ds-tooltip-bubble-arrow-width);
      height: var(--ds-tooltip-bubble-arrow-height);
      background: var(--ds-tooltip-bubble-background);
      z-index: 3;
    }

    .ds-tooltip__bubble--top .ds-tooltip__surface::before,
    .ds-tooltip__bubble--top .ds-tooltip__surface::after,
    .ds-tooltip__bubble--bottom .ds-tooltip__surface::before,
    .ds-tooltip__bubble--bottom .ds-tooltip__surface::after {
      left: var(--ds-tooltip-arrow-x);
    }

    .ds-tooltip__bubble--left .ds-tooltip__surface::before,
    .ds-tooltip__bubble--left .ds-tooltip__surface::after,
    .ds-tooltip__bubble--right .ds-tooltip__surface::before,
    .ds-tooltip__bubble--right .ds-tooltip__surface::after {
      top: var(--ds-tooltip-arrow-y);
    }

    .ds-tooltip__bubble--top .ds-tooltip__surface::before,
    .ds-tooltip__bubble--bottom .ds-tooltip__surface::before {
      left: calc(var(--ds-tooltip-arrow-x) - var(--ds-tooltip-arrow-border-visual-width));
      width: calc(var(--ds-tooltip-bubble-arrow-width) + var(--ds-tooltip-arrow-border-visual-width) * 2);
      height: calc(var(--ds-tooltip-bubble-arrow-height) + var(--ds-tooltip-arrow-border-visual-width));
    }

    .ds-tooltip__bubble--left .ds-tooltip__surface::before,
    .ds-tooltip__bubble--right .ds-tooltip__surface::before {
      top: calc(var(--ds-tooltip-arrow-y) - var(--ds-tooltip-arrow-border-visual-width));
      width: calc(var(--ds-tooltip-bubble-arrow-height) + var(--ds-tooltip-arrow-border-visual-width));
      height: calc(var(--ds-tooltip-bubble-arrow-width) + var(--ds-tooltip-arrow-border-visual-width) * 2);
    }

    .ds-tooltip__bubble--left .ds-tooltip__surface::after,
    .ds-tooltip__bubble--right .ds-tooltip__surface::after {
      width: var(--ds-tooltip-bubble-arrow-height);
      height: var(--ds-tooltip-bubble-arrow-width);
    }

    .ds-tooltip__bubble--top .ds-tooltip__surface::before,
    .ds-tooltip__bubble--top .ds-tooltip__surface::after {
      clip-path: polygon(0 0, 100% 0, 50% 100%);
    }

    .ds-tooltip__bubble--top .ds-tooltip__surface::before {
      bottom: calc((var(--ds-tooltip-bubble-arrow-height) + var(--ds-tooltip-arrow-border-visual-width)) * -1 + var(--ds-tooltip-arrow-overlap));
    }

    .ds-tooltip__bubble--top .ds-tooltip__surface::after {
      bottom: calc(var(--ds-tooltip-bubble-arrow-height) * -1 + var(--ds-tooltip-arrow-overlap));
    }

    .ds-tooltip__bubble--bottom .ds-tooltip__surface::before,
    .ds-tooltip__bubble--bottom .ds-tooltip__surface::after {
      clip-path: polygon(50% 0, 0 100%, 100% 100%);
    }

    .ds-tooltip__bubble--bottom .ds-tooltip__surface::before {
      top: calc((var(--ds-tooltip-bubble-arrow-height) + var(--ds-tooltip-arrow-border-visual-width)) * -1 + var(--ds-tooltip-arrow-overlap));
    }

    .ds-tooltip__bubble--bottom .ds-tooltip__surface::after {
      top: calc(var(--ds-tooltip-bubble-arrow-height) * -1 + var(--ds-tooltip-arrow-overlap));
    }

    .ds-tooltip__bubble--left .ds-tooltip__surface::before,
    .ds-tooltip__bubble--left .ds-tooltip__surface::after {
      clip-path: polygon(0 0, 100% 50%, 0 100%);
    }

    .ds-tooltip__bubble--left .ds-tooltip__surface::before {
      right: calc((var(--ds-tooltip-bubble-arrow-height) + var(--ds-tooltip-arrow-border-visual-width)) * -1 + var(--ds-tooltip-arrow-overlap));
    }

    .ds-tooltip__bubble--left .ds-tooltip__surface::after {
      right: calc(var(--ds-tooltip-bubble-arrow-height) * -1 + var(--ds-tooltip-arrow-overlap));
    }

    .ds-tooltip__bubble--right .ds-tooltip__surface::before,
    .ds-tooltip__bubble--right .ds-tooltip__surface::after {
      clip-path: polygon(100% 0, 0 50%, 100% 100%);
    }

    .ds-tooltip__bubble--right .ds-tooltip__surface::before {
      left: calc((var(--ds-tooltip-bubble-arrow-height) + var(--ds-tooltip-arrow-border-visual-width)) * -1 + var(--ds-tooltip-arrow-overlap));
    }

    .ds-tooltip__bubble--right .ds-tooltip__surface::after {
      left: calc(var(--ds-tooltip-bubble-arrow-height) * -1 + var(--ds-tooltip-arrow-overlap));
    }
  `],
})
export class DsTooltipComponent implements AfterViewInit, OnDestroy {
  @Input() title = '';
  @Input() text =
    'El monto de aporte y rendimiento, es distinto a tu saldo total debido a que se obtiene del mes anterior.';
  @Input() markerColor = '';
  @Input() placement: DsTooltipPlacement = 'auto';
  @Input() arrowAlign: DsTooltipArrowAlign | 'auto' = 'auto';
  @Input() ariaLabel = 'Mostrar informacion';

  @ViewChild('button') private button?: ElementRef<HTMLButtonElement>;
  @ViewChild('bubble') private bubble?: ElementRef<HTMLElement>;

  open = false;
  resolvedPlacement: Exclude<DsTooltipPlacement, 'auto'> = 'top';
  resolvedAlign: DsTooltipArrowAlign = 'center';
  bubbleLeft = 0;
  bubbleTop = 0;
  arrowX = 0;
  arrowY = 0;
  private readonly scrollListener = (): void => {
    if (this.open) {
      this.updatePosition();
    }
  };

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  @HostBinding('class.ds-tooltip-host--open')
  get hostOpen(): boolean {
    return this.open;
  }

  ngAfterViewInit(): void {
    document.addEventListener('scroll', this.scrollListener, true);
    this.updatePosition();
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.scrollListener, true);
  }

  get hasTitle(): boolean {
    return this.title.trim().length > 0;
  }

  get hasMarker(): boolean {
    return this.markerColor.trim().length > 0;
  }

  get bubbleClasses(): string {
    return [
      `ds-tooltip__bubble--${this.resolvedPlacement}`,
      `ds-tooltip__bubble--align-${this.resolvedAlign}`,
    ].join(' ');
  }

  get arrowXStyle(): string {
    return `${this.arrowX}px`;
  }

  get arrowYStyle(): string {
    return `${this.arrowY}px`;
  }

  get bubbleLeftStyle(): string {
    return `${this.bubbleLeft}px`;
  }

  get bubbleTopStyle(): string {
    return `${this.bubbleTop}px`;
  }

  toggle(): void {
    this.open = !this.open;

    if (this.open) {
      this.updatePosition();
      this.schedulePositionUpdate();
    }

    this.cdr.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  closeFromOutside(event: MouseEvent): void {
    if (!this.open || this.host.nativeElement.contains(event.target as Node)) {
      return;
    }

    this.open = false;
    this.cdr.markForCheck();
  }

  @HostListener('document:keydown.escape')
  closeFromEscape(): void {
    if (!this.open) {
      return;
    }

    this.open = false;
    this.cdr.markForCheck();
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  updatePosition(): void {
    const button = this.button?.nativeElement;
    if (!button) {
      return;
    }

    const bubble = this.bubble?.nativeElement;
    if (!bubble) {
      return;
    }

    const rect = button.getBoundingClientRect();
    const bubbleWidth = bubble.offsetWidth;
    const bubbleHeight = bubble.offsetHeight;
    if (bubbleWidth === 0 || bubbleHeight === 0) {
      return;
    }

    const gap = 14;
    const visibleBounds = this.visibleBounds();

    this.resolvedPlacement = this.resolvePlacement(rect, bubbleWidth, bubbleHeight, gap, visibleBounds);
    this.resolvedAlign = this.resolveAlign(rect, bubbleWidth, bubbleHeight, visibleBounds);
    this.updateBubbleAndArrowOffset(rect, bubbleWidth, bubbleHeight, visibleBounds);
    this.cdr.markForCheck();
  }

  private resolvePlacement(
    rect: DOMRect,
    bubbleWidth: number,
    bubbleHeight: number,
    gap: number,
    visibleBounds: DsTooltipVisibleBounds,
  ): Exclude<DsTooltipPlacement, 'auto'> {
    if (this.placement !== 'auto') {
      return this.placement;
    }

    const space = {
      top: rect.top - visibleBounds.top,
      bottom: visibleBounds.bottom - rect.bottom,
      left: rect.left - visibleBounds.left,
      right: visibleBounds.right - rect.right,
    };

    if (space.top >= bubbleHeight + gap) return 'top';
    if (space.bottom >= bubbleHeight + gap) return 'bottom';
    if (space.right >= bubbleWidth + gap) return 'right';
    if (space.left >= bubbleWidth + gap) return 'left';

    return Object.entries(space).sort((a, b) => b[1] - a[1])[0][0] as Exclude<DsTooltipPlacement, 'auto'>;
  }

  private resolveAlign(
    rect: DOMRect,
    bubbleWidth: number,
    bubbleHeight: number,
    visibleBounds: DsTooltipVisibleBounds,
  ): DsTooltipArrowAlign {
    if (this.arrowAlign !== 'auto') {
      return this.arrowAlign;
    }

    if (this.resolvedPlacement === 'top' || this.resolvedPlacement === 'bottom') {
      if (rect.left + bubbleWidth > visibleBounds.right) return 'end';
      if (rect.right - bubbleWidth < visibleBounds.left) return 'start';
      return 'center';
    }

    if (rect.top + bubbleHeight > visibleBounds.bottom) return 'end';
    if (rect.bottom - bubbleHeight < visibleBounds.top) return 'start';
    return 'center';
  }

  private updateBubbleAndArrowOffset(
    rect: DOMRect,
    bubbleWidth: number,
    bubbleHeight: number,
    visibleBounds: DsTooltipVisibleBounds,
  ): void {
    const arrowWidth = this.cssLength('--ds-tooltip-bubble-arrow-width', 10);
    const arrowHeight = this.cssLength('--ds-tooltip-bubble-arrow-height', 8);
    const arrowInset = this.cssLength('--ds-tooltip-bubble-arrow-inset', 20);
    const topBottomArrowWidth = arrowWidth;
    const topBottomArrowHeight = arrowHeight;
    const sideArrowWidth = arrowHeight;
    const sideArrowHeight = arrowWidth;
    const arrowOverlap = 2;
    const sideVerticalOffset = sideArrowHeight;
    const buttonWidth = rect.width;
    const buttonHeight = rect.height;

    switch (this.resolvedPlacement) {
      case 'top':
        {
          const horizontal = this.bubbleAndArrowOffset(
            buttonWidth / 2,
            this.bubbleInlineOffset(buttonWidth, bubbleWidth),
            bubbleWidth,
            topBottomArrowWidth,
            arrowInset,
            visibleBounds.left - rect.left,
            visibleBounds.right - bubbleWidth - rect.left,
          );
          this.bubbleLeft = horizontal.bubbleOffset;
          this.arrowX = horizontal.arrowOffset;
        }
        this.bubbleTop = -bubbleHeight - topBottomArrowHeight + arrowOverlap;
        this.arrowY = bubbleHeight - topBottomArrowHeight + arrowOverlap;
        return;
      case 'bottom':
        {
          const horizontal = this.bubbleAndArrowOffset(
            buttonWidth / 2,
            this.bubbleInlineOffset(buttonWidth, bubbleWidth),
            bubbleWidth,
            topBottomArrowWidth,
            arrowInset,
            visibleBounds.left - rect.left,
            visibleBounds.right - bubbleWidth - rect.left,
          );
          this.bubbleLeft = horizontal.bubbleOffset;
          this.arrowX = horizontal.arrowOffset;
        }
        this.bubbleTop = buttonHeight + topBottomArrowHeight - arrowOverlap;
        this.arrowY = -topBottomArrowHeight + arrowOverlap;
        return;
      case 'left':
        this.bubbleLeft = -bubbleWidth - sideArrowWidth + arrowOverlap;
        {
          const vertical = this.bubbleAndArrowOffset(
            buttonHeight / 2,
            this.bubbleBlockOffset(buttonHeight, bubbleHeight) + sideVerticalOffset,
            bubbleHeight,
            sideArrowHeight,
            arrowInset,
            visibleBounds.top - rect.top,
            visibleBounds.bottom - bubbleHeight - rect.top,
          );
          this.bubbleTop = vertical.bubbleOffset;
          this.arrowY = vertical.arrowOffset;
        }
        this.arrowX = bubbleWidth - sideArrowWidth + arrowOverlap;
        return;
      case 'right':
        this.bubbleLeft = buttonWidth + sideArrowWidth - arrowOverlap;
        {
          const vertical = this.bubbleAndArrowOffset(
            buttonHeight / 2,
            this.bubbleBlockOffset(buttonHeight, bubbleHeight) + sideVerticalOffset,
            bubbleHeight,
            sideArrowHeight,
            arrowInset,
            visibleBounds.top - rect.top,
            visibleBounds.bottom - bubbleHeight - rect.top,
          );
          this.bubbleTop = vertical.bubbleOffset;
          this.arrowY = vertical.arrowOffset;
        }
        this.arrowX = -sideArrowWidth + arrowOverlap;
        return;
    }
  }

  private bubbleInlineOffset(buttonWidth: number, bubbleWidth: number): number {
    switch (this.resolvedAlign) {
      case 'start':
        return 0;
      case 'end':
        return buttonWidth - bubbleWidth;
      case 'center':
      default:
        return (buttonWidth - bubbleWidth) / 2;
    }
  }

  private bubbleBlockOffset(buttonHeight: number, bubbleHeight: number): number {
    switch (this.resolvedAlign) {
      case 'start':
        return 0;
      case 'end':
        return buttonHeight - bubbleHeight;
      case 'center':
      default:
        return (buttonHeight - bubbleHeight) / 2;
    }
  }

  private constrainBubbleLeft(
    rect: DOMRect,
    bubbleLeft: number,
    bubbleWidth: number,
    visibleBounds: DsTooltipVisibleBounds,
  ): number {
    const viewportLeft = rect.left + bubbleLeft;
    const constrainedLeft = this.clampToRange(
      viewportLeft,
      visibleBounds.left,
      visibleBounds.right - bubbleWidth,
    );
    return constrainedLeft - rect.left;
  }

  private constrainBubbleTop(
    rect: DOMRect,
    bubbleTop: number,
    bubbleHeight: number,
    visibleBounds: DsTooltipVisibleBounds,
  ): number {
    const viewportTop = rect.top + bubbleTop;
    const constrainedTop = this.clampToRange(
      viewportTop,
      visibleBounds.top,
      visibleBounds.bottom - bubbleHeight,
    );
    return constrainedTop - rect.top;
  }

  private visibleBounds(): DsTooltipVisibleBounds {
    let bounds = this.rectToBounds({
      top: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
      left: 0,
      width: window.innerWidth,
      height: window.innerHeight,
    } as DOMRect);

    let current: HTMLElement | null = this.host.nativeElement.parentElement;
    while (current) {
      const styles = window.getComputedStyle(current);
      const overflow = `${styles.overflow} ${styles.overflowX} ${styles.overflowY}`;

      if (/(auto|scroll|hidden|clip)/.test(overflow)) {
        bounds = this.intersectBounds(bounds, this.rectToBounds(current.getBoundingClientRect()));
      }

      current = current.parentElement;
    }

    return bounds;
  }

  private rectToBounds(rect: DOMRect): DsTooltipVisibleBounds {
    return {
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      left: rect.left,
      width: rect.right - rect.left,
      height: rect.bottom - rect.top,
    };
  }

  private intersectBounds(
    a: DsTooltipVisibleBounds,
    b: DsTooltipVisibleBounds,
  ): DsTooltipVisibleBounds {
    const left = Math.max(a.left, b.left);
    const top = Math.max(a.top, b.top);
    const right = Math.min(a.right, b.right);
    const bottom = Math.min(a.bottom, b.bottom);

    return {
      top,
      right,
      bottom,
      left,
      width: Math.max(0, right - left),
      height: Math.max(0, bottom - top),
    };
  }

  private cssLength(variableName: string, fallback: number): number {
    const rawValue = getComputedStyle(this.host.nativeElement).getPropertyValue(variableName).trim();
    const numericValue = Number.parseFloat(rawValue);
    return Number.isFinite(numericValue) ? numericValue : fallback;
  }

  private schedulePositionUpdate(): void {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => this.updatePosition());
      return;
    }

    queueMicrotask(() => this.updatePosition());
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  private clampToRange(value: number, min: number, max: number): number {
    return this.clamp(value, Math.min(min, max), Math.max(min, max));
  }

  private arrowOffset(
    anchorCenter: number,
    bubbleOffset: number,
    bubbleSize: number,
    arrowSize: number,
    arrowInset: number,
  ): number {
    const edgeInset = Math.max(arrowSize / 2, arrowInset);
    return this.clamp(
      anchorCenter - bubbleOffset - arrowSize / 2,
      edgeInset,
      Math.max(edgeInset, bubbleSize - arrowSize - edgeInset),
    );
  }

  private bubbleAndArrowOffset(
    anchorCenter: number,
    preferredBubbleOffset: number,
    bubbleSize: number,
    arrowSize: number,
    arrowInset: number,
    minBubbleOffset: number,
    maxBubbleOffset: number,
  ): { bubbleOffset: number; arrowOffset: number } {
    const edgeInset = Math.max(arrowSize / 2, arrowInset);
    const minArrowOffset = edgeInset;
    const maxArrowOffset = Math.max(edgeInset, bubbleSize - arrowSize - edgeInset);
    const constrainedBubbleOffset = this.clampToRange(
      preferredBubbleOffset,
      minBubbleOffset,
      maxBubbleOffset,
    );
    const constrainedArrowOffset = this.clamp(
      anchorCenter - constrainedBubbleOffset - arrowSize / 2,
      minArrowOffset,
      maxArrowOffset,
    );
    const bubbleOffset = this.clampToRange(
      anchorCenter - constrainedArrowOffset - arrowSize / 2,
      minBubbleOffset,
      maxBubbleOffset,
    );

    return {
      bubbleOffset,
      arrowOffset: this.arrowOffset(anchorCenter, bubbleOffset, bubbleSize, arrowSize, arrowInset),
    };
  }
}
